import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';
import { hoy, toLocalISO } from '@/app/core';

@Component({
  selector: 'c-filter',
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.css',
  standalone: false,
})
export class FilterComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly debounceSearch = new Subject<{
    key: string;
    value: any;
    args: any[];
  }>();
  readonly maxFecha = hoy();

  /**
   * @description Inicializa el flujo con debounce aplicando un retraso de 500ms,
   * ejecuta actualización con filtros -> evita sobrecargar el servidor
   */
  constructor() {
    this.debounceSearch
      .pipe(debounceTime(500), takeUntilDestroyed())
      .subscribe(({ key, value, args }) => {
        this.executeChangeInput(key, value, ...args);
      });
  }

  clearFilters() {
    const params = this.route.snapshot.queryParams;
    const newParams = { ...params };

    this.filtros.forEach((filtro) => {
      if (filtro.disable) return;

      let normalizedValue: string | string[] | undefined;

      // ---------- RESET UI ----------
      if (filtro.showAlwaysValues) {
        filtro.value = filtro.options;

        normalizedValue = filtro.options?.map((o: any) =>
          String(o[filtro.searchProperty ?? '']),
        );
      } else {
        filtro.value = undefined;
        normalizedValue = undefined;

        // emitir reset para tablas u otros listeners
        this.event.emit({ key: filtro.query, value: '', reset: true });
      }

      // ---------- QUERY PARAMS ----------
      if (
        normalizedValue == null ||
        (Array.isArray(normalizedValue) && normalizedValue.length === 0)
      ) {
        delete newParams[filtro.query];
      } else {
        newParams[filtro.query] = Array.isArray(normalizedValue)
          ? normalizedValue.join(',')
          : normalizedValue;
      }
    });

    // ---------- UNA SOLA NAVEGACIÓN ----------
    this.router.navigate([], {
      queryParams: newParams,
      replaceUrl: true,
    });
  }

  async ngAfterViewInit() {
    setTimeout(async () => {
      const params = await firstValueFrom(this.route.queryParams);
      Object.keys(params).forEach((key) => {
        const filter = this.filtros.find((filtro) => filtro.query === key);
        if (filter) {
          let value;
          try {
            value = JSON.parse(params[key]);
          } catch (e) {
            value = params[key];
          }
          value = Array.isArray(value) ? value : value.toString();

          filter.value = filter.options
            ? filter.type === 'multiselect'
              ? filter.options.filter((op: any) =>
                  filter.searchProperty
                    ? value.includes(op[filter.searchProperty!])
                    : op == value,
                )
              : filter.options.find((op: any) =>
                  filter.searchProperty
                    ? op[filter.searchProperty!] == value
                    : op == value,
                )
            : filter.type === 'date'
              ? new Date(value)
              : value;

          // Al cargar parámetros de la URL inicial, usamos executeChangeInput para no causar retrasos visuales
          this.executeChangeInput(key, value);
        }
      });
    }, 150);
  }

  @Input() filtros: any[] = [];

  @Output('changeInput') private event = new EventEmitter<{
    key: string;
    value: string | undefined;
    reset?: boolean;
  }>();

  /**
   * @description Determina automáticamente si el filtro es de tipo texto para
   * aplicar un retraso; si es de otro tipo, filtra inmediatamente
   */
  changeInput(
    key: string,
    value: string | any[],
    showAlwaysValues?: boolean,
    options?: any[],
    searchProperty?: string,
  ) {
    const filtro = this.filtros.find((f) => f.query === key);

    if (filtro?.type === 'input') {
      // Si es un input de texto, retrasamos la ejecución
      this.debounceSearch.next({
        key,
        value,
        args: [showAlwaysValues, options, searchProperty],
      });
    } else {
      // Si es dropdown, fecha o multiselect, se ejecuta inmediatamente
      this.executeChangeInput(
        key,
        value,
        showAlwaysValues,
        options,
        searchProperty,
      );
    }
  }

  /**
   * @description Normaliza valores entrantes y emite evento al componente para
   * actualizar la tabla
   */
  private executeChangeInput(
    key: string,
    value: string | any[],
    showAlwaysValues?: boolean,
    options?: any[],
    searchProperty?: string,
  ) {
    let normalizedValue: string | string[] | undefined;

    if (value instanceof Date) {
      value = isNaN(value.getTime()) ? '' : toLocalISO(value);
    }

    if (
      showAlwaysValues &&
      (Array.isArray(value)
        ? value.length === 0
        : value === '' || value == null)
    ) {
      normalizedValue = options?.map((o) => String(o[searchProperty ?? '']));
    } else {
      normalizedValue = Array.isArray(value)
        ? value.map((v) =>
            searchProperty
              ? typeof v == 'object'
                ? String(v[searchProperty])
                : v
              : String(v),
          )
        : value;
    }

    this.event.emit({
      key,
      value: Array.isArray(normalizedValue)
        ? normalizedValue.join(',')
        : normalizedValue,
    });

    const params = this.route.snapshot.queryParams;
    const newParams = { ...params };

    // ---- Query Params ----
    if (
      normalizedValue == null ||
      (Array.isArray(normalizedValue) && normalizedValue.length === 0) ||
      normalizedValue === ''
    ) {
      delete newParams[key];
    } else {
      newParams[key] = Array.isArray(normalizedValue)
        ? normalizedValue.join(',')
        : normalizedValue;
    }

    // ---- Una sola navegación ----
    this.router.navigate([], {
      queryParams: newParams,
      replaceUrl: true,
    });
  }

  ngOnDestroy() {
    this.router.navigate([], {
      queryParams: {}, // 🔹 Elimina todos los query params
      queryParamsHandling: 'merge', // 🔹 Evita que los anteriores se mantengan
      replaceUrl: true,
    });
  }

  returnMap(options: any, searchProperty: string | undefined) {
    if (!searchProperty) return;
    return options.map((option: any) => option[searchProperty]);
  }
}
